import React, { useState, useEffect, useCallback } from "react";
import { isNullOrEmptyArray } from "src/util/arrays";
import { GenericModal } from "../../components/Modal";
import GenericCollapsibleList from "../../sections/GenericCollapsibleList";
import * as GenericUi from "../../components/Modal/styles";
import * as Ui from "./styles";
import { ChainIcon, InfoIconEvolutive, SendIcon } from "src/components/Icons";
import { InputText } from "src/components";

const itemProps = {
  width: "100%",
  withTag: true,
  maxHeight: "unset",
  renderStyle: "sendModalCombination",
};

export const SendCombinationModal = function ({
  // Array de combinações
  groupValues = [],
  onSave,
  onCancel,
  // Array de objetos que corresponde as perguntas enviadas
  questionsSent = [],
  ...props
}) {
  // states
  const [internalValues, setInternalValues] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});
  const [itemId, setItemId] = useState(-1);
  const [clickable, setClickable] = useState(false);

  // Renderiza um componente referente ao conteúdo a direita do itens
  const RightContent = useCallback((combinationLength) => {
    return (
      <GenericUi.Label withIcon className="rightContent">
        <ChainIcon />{combinationLength} combinações
      </GenericUi.Label>
    );
  }, []);

  /**
   * Effects: Seta o valor inicial do grupo de perguntas combinadas
   */
  useEffect(() => {
    if (
      !isNullOrEmptyArray(groupValues) &&
      isNullOrEmptyArray(internalValues)
    ) {
      const parsedValues = groupValues.map((groupItem) => ({
        id: groupItem.id,
        mainTitle: groupItem.title || groupItem.group[0].mainTitle,
        group: groupItem.group,
      }));
      setInternalValues(parsedValues);
    }
  }, [groupValues, internalValues, setInternalValues]);

  /**
   * Effects: Seta o valor do grupo selecionado no handle da radio button
   */
  useEffect(() => {
    if (!isNullOrEmptyArray(groupValues)) {
      const [filteredGroup] = groupValues.filter((groupItem) => groupItem.id === itemId);
      setSelectedItem(filteredGroup || {});
    }
  }, [itemId, setSelectedItem, groupValues])

  /**
   * Callbacks: Seta o novo estado de seleção dos itens
   */
  const handleSelectionChange = useCallback((sel) => {
    setItemId(sel[0]?.id || -1);
  }, [setItemId]);

  useEffect(() => {
    if(itemId !== -1) {
      setClickable(true);
    } else {
      setClickable(false);
    }
  }, [itemId])

  /**
   * Callbacks: Renderiza o conteúdo da direita do item (x combinações) de forma a obter a quantidade de grupos
   * de forma dinâmica.
   */
  const getConditionalItemProps = useCallback((context) => {
    const { id: contextId } = context;

    return {
      // Bind: estamos passando a quantidade de grupos do item de forma dinâmica, isto é, o componente reconhece seu próprio
      // contexto.
      RightContent: RightContent.bind(null, groupValues.find(({ id }) => id === contextId).group.length),
    }
  }, [groupValues]);

  /**
   * Callbacks: Renderiza a listagem de perguntas combinadas
   */
  const renderGroupItems = useCallback(() => {
    return (
      <GenericCollapsibleList
        data={internalValues}
        withControlledItems
        itemCheckboxStyle="circle"
        onChangeSelection={handleSelectionChange}
        customItemProps={{ ...itemProps }}
        itemConditionalRenderer={getConditionalItemProps}
      />
    );
  }, [groupValues, internalValues]);

  /**
   * Callbacks: Executa a instrução de fechar modal abortando
   * a operação
   */
  const onClose = useCallback(() => {
    if (onCancel) onCancel();
  }, [onCancel]);

  /**
   * Callbacks: Executa a instrução de salvar as alterações
   * feitas no modal
   */
  const onOk = useCallback(() => {
    if (onSave) onSave(selectedItem, questionsSent);    
  }, [onSave, selectedItem, questionsSent]);
  /**
   * Callbacks: Renderiza o icone de do titulo do modal
   */
  const SendIconTitle = useCallback(() => <Ui.IconSend />, []);

  /**
   * Callbacks: Renderiza a lista de perguntas avulasas
   * a ser inserida em um grupo
   */
  const renderQuestionSent = useCallback(() => (
    <Ui.QuestionContent>
      {questionsSent.length &&
        questionsSent.map(({ mainTitle, id }) => (
          <InputText key={id} placeholder={mainTitle} disabled />
        ))}
    </Ui.QuestionContent>
  ), []);

  return (
    <GenericModal
      title={"Enviar pergunta para um grupo de perguntas combinadas existente"}
      onCancel={onClose}
      onOk={onOk}
      isOpen
      tooltipTitle="Na lista a seguir, selecione o grupo de perguntas para o qual deseja enviar a pergunta selecionada"
      okBtnTitle="ENVIAR"
      cancelBtnTitle="CANCELAR"
      iconComponent={<SendIconTitle />}
      okBtnClickable={clickable}
    >
      <GenericUi.FixedItem>
        <GenericUi.Label withIcon>
          <InfoIconEvolutive />
          {"Pergunta selecionada"}
        </GenericUi.Label>
        {renderQuestionSent()}
      </GenericUi.FixedItem>
      <GenericUi.FixedItem>
        <GenericUi.Label withIcon>
          <InfoIconEvolutive />
          {"Escolha uma combinação para adicionar a pergunta selecionada"}
        </GenericUi.Label>
        {renderGroupItems()}
      </GenericUi.FixedItem>
    </GenericModal>
  );
};
